/**
 * The narrator's lines, grouped by the job they are about.
 *
 * `peterhof_orch` reports what its HELPER is doing while the helper does it: a
 * closure it hit, a route it rebuilt, a source that went quiet. Each line is an
 * ordinary `pa_bridge` bubble carrying `bridgeKind: 'progress'`, the
 * `delegationId` of the `delegate` call it belongs to and a `progressSeq`.
 *
 * Two things this module decides, and neither needs a store:
 *
 * **Grouping.** Consecutive progress bubbles of ONE delegation become one item,
 * ordered by `progressSeq` — the order the visitor read them in live, which a
 * reloaded thread has to reproduce and the checkpoint does not promise. A run of
 * lines is broken by any other bubble, so two delegations (or a delegation and
 * the answer between them) can never fold into one group.
 *
 * **Collapsing.** While the turn is still running the lines stand open: they are
 * the only thing to read. Once the turn's ANSWER has arrived the group closes
 * into one summary line — the job is over and its commentary is history, which
 * the visitor may still want but is no longer waiting for. «The answer has
 * arrived» is read positionally: any later guide bubble that is neither a
 * progress line nor the `say` handoff. That reading survives a reload, where
 * `turn` is `0` on every restored bubble and nothing else is left to key on.
 *
 * Pure functions over the bubble list, called from `Thread`'s render — there is
 * deliberately no second state store for this: the store already holds
 * everything, and a derived copy of it is how two readers of one turn start
 * disagreeing about which bubbles belong to which job.
 */

import type { Bubble } from '../store/chatStore';

export const PROGRESS_KIND = 'progress';

/** One thing `Thread` draws: a bubble, or a whole delegation's commentary. */
export type ThreadItem =
  | { kind: 'bubble'; bubble: Bubble }
  | { kind: 'progress'; key: string; delegationId: string; lines: Bubble[]; collapsed: boolean };

export function isProgress(bubble: Bubble): boolean {
  return bubble.role === 'guide' && bubble.bridgeKind === PROGRESS_KIND;
}

/**
 * `true` once the turn's answer stands after `index` — the cue to collapse.
 *
 * A guide bubble that is neither a progress line nor a bridge: the `say` handoff
 * is a bridge and stands BEFORE the group anyway, and a user bubble means the
 * visitor moved on, which closes the job just as finally as an answer does.
 */
function answered(bubbles: Bubble[], index: number): boolean {
  return bubbles
    .slice(index)
    .some((bubble) => (bubble.role === 'guide' && !isProgress(bubble) && !bubble.bridge) || bubble.role === 'user');
}

/** The bubble list as items: progress runs folded, everything else untouched. */
export function threadItems(bubbles: Bubble[]): ThreadItem[] {
  const items: ThreadItem[] = [];
  for (let at = 0; at < bubbles.length; ) {
    const bubble = bubbles[at]!;
    if (!isProgress(bubble)) {
      items.push({ kind: 'bubble', bubble });
      at += 1;
      continue;
    }
    const delegationId = bubble.delegationId ?? '';
    let end = at;
    while (end < bubbles.length && isProgress(bubbles[end]!) && (bubbles[end]!.delegationId ?? '') === delegationId) {
      end += 1;
    }
    // A STABLE sort on `progressSeq`: two lines that somehow share a seq keep
    // the order they arrived in rather than swapping between renders.
    const lines = bubbles.slice(at, end).sort((a, b) => (a.progressSeq ?? 0) - (b.progressSeq ?? 0));
    items.push({
      kind: 'progress',
      key: `progress:${delegationId}:${lines[0]!.id}`,
      delegationId,
      lines,
      collapsed: answered(bubbles, end),
    });
    at = end;
  }
  return items;
}

/** The collapsed group's one line. */
export function progressSummary(count: number): string {
  return `Что делал помощник · ${count}`;
}
